<template>
  <section class="summary" aria-label="Resumen de la importación">
    <!-- Los íconos se dibujan una vez; al cambiar el tono (:key) se vuelven a dibujar. -->
    <div class="stats">
      <div v-for="stat in stats" :key="stat.key" class="stat" :class="`tone-${stat.tone}`">
        <span class="stat__icon">
          <LottiePlayer :key="`${stat.key}-${stat.tone}`" :animation="icons[stat.icon]" :loop="false" class="stat__art" />
        </span>
        <span class="stat__text">
          <span class="stat__value">{{ stat.value }}</span>
          <span class="stat__label">{{ stat.label }}</span>
        </span>
      </div>
    </div>

    <!-- Un slot por banner (nombre = banner.key) para los botones de la pantalla. -->
    <div v-for="banner in banners" :key="`${banner.key}-${banner.tone}`" class="banner" :class="`tone-${banner.tone}`" role="status">
      <span class="banner__icon"><LottiePlayer :animation="bannerIcon(banner)" :loop="false" class="banner__art" /></span>
      <div class="banner__text">
        <div class="banner__title">{{ banner.title }}</div>
        <div class="banner__detail">{{ banner.detail }}</div>
      </div>
      <span v-if="$scopedSlots[banner.key]" class="banner__action"><slot :name="banner.key" :banner="banner"></slot></span>
    </div>

    <!-- Corrección en bloque: el mismo error de catálogo en 2 o más filas se corrige con una sola elección. -->
    <div v-if="bulkGroups.length" class="banner tone-warning bulk">
      <span class="banner__icon"><LottiePlayer :animation="icons.alert" :loop="false" class="banner__art" /></span>
      <div class="banner__text">
        <div class="banner__title">Errores que se repiten</div>
        <div class="banner__detail">Elija el valor correcto y aplíquelo a todas esas filas.</div>
      </div>
      <div class="bulk__rows">
        <div v-for="group in bulkGroups" :key="group.key" class="bulk__row">
          <span class="bulk__label">{{ group.label }} · <strong>{{ group.count }} filas</strong></span>
          <v-autocomplete
            :value="bulkChoice[group.key]"
            :items="group.items"
            label="Usar"
            :aria-label="`Usar para ${group.label}`"
            class="bulk__select"
            dense
            outlined
            hide-details
            @change="$set(bulkChoice, group.key, $event)"
          ></v-autocomplete>
          <v-btn small color="green darken-3" class="white--text" :aria-label="`Aplicar a ${group.count} filas: ${group.label}`" :disabled="!bulkChoice[group.key]" @click="apply(group)">Aplicar a {{ group.count }}</v-btn>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import LottiePlayer from "@/components/LottiePlayer";
import sheetIcon from "@/assets/lottie/icon-sheet.json";
import checkIcon from "@/assets/lottie/icon-check.json";
import alertIcon from "@/assets/lottie/icon-alert.json";

// Resumen de los importadores V2: tarjetas, avisos y corrección en bloque. Solo presenta: los números y textos los calcula la pantalla.
// stats: [{ key, label, value, tone, icon: "sheet" | "check" | "alert" }] · banners: [{ key, tone, title, detail }]
// bulkGroups: [{ key, label, count, items }]. Evento apply-bulk({ group, value }).
export default {
  name: "ImportSummary",
  components: { LottiePlayer },
  props: {
    stats: { type: Array, default: () => [] },
    banners: { type: Array, default: () => [] },
    bulkGroups: { type: Array, default: () => [] },
  },
  data() {
    return { bulkChoice: {} };
  },
  created() {
    // JSON de Lottie sin reactividad: Vue no necesita observar miles de nodos.
    this.icons = { sheet: sheetIcon, check: checkIcon, alert: alertIcon };
  },
  methods: {
    bannerIcon(banner) {
      return { success: this.icons.check, info: this.icons.sheet }[banner.tone] || this.icons.alert;
    },
    apply(group) {
      const value = this.bulkChoice[group.key];
      if (!value) return;
      this.$emit("apply-bulk", { group, value });
      this.$delete(this.bulkChoice, group.key);
    },
  },
};
</script>

<style scoped>
.summary {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 28px;
}

/* Cada tono fija el fondo del círculo y el color del texto. */
.tone-info {
  --tone-bg: #e3f0fa;
  --tone-ink: #1b4f72;
}

.tone-success {
  --tone-bg: #e6f6ec;
  --tone-ink: #14532d;
}

.tone-danger {
  --tone-bg: #fdecea;
  --tone-ink: #922b21;
}

.tone-warning {
  --tone-bg: #fff4e5;
  --tone-ink: #7e5109;
}

.tone-muted {
  --tone-bg: #eef1f4;
  --tone-ink: rgba(0, 0, 0, 0.6);
}

.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 16px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background-color: #f4f9fd;
}

.stat__icon,
.banner__icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 50%;
  background-color: var(--tone-bg);
}

.stat__icon {
  width: 52px;
  height: 52px;
}

.stat__art {
  width: 32px;
  height: 32px;
}

.tone-muted .stat__art {
  filter: grayscale(1);
}

.stat__text {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
}

.stat__value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--tone-ink);
  font-variant-numeric: tabular-nums;
}

.stat__label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.65);
}

.banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background-color: var(--tone-bg);
}

.banner__icon {
  width: 44px;
  height: 44px;
  background-color: rgba(255, 255, 255, 0.7);
}

.banner__art {
  width: 28px;
  height: 28px;
}

.banner__text {
  flex: 1 1 160px;
}

.banner__action {
  margin-left: auto;
}

.banner__title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--tone-ink);
}

.banner__detail {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.72);
}

.bulk__rows {
  flex: 1 1 100%;
  padding: 12px 14px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.7);
}

.bulk__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 4px 0;
}

.bulk__label {
  flex: 1 1 220px;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.87);
}

.bulk__select {
  flex: 0 1 260px;
  min-width: 200px;
}
</style>
